<template>
  <div v-if="show" class="job-detail-wrapper">
    <div class="job-detail-container">
      <div class="job-info">
        <div class="container">
          <div class="header" ref="jobDetailHeaderRef">
            <el-link class="back-to-job-list" :underline="false" :disabled="isPreview" @click="handleBackToJobList">
              <i class="back-job-list-icon el-icon-arrow-left"></i>
              <span class="label">{{ $t(`${i18nPrefix}.label.backToJobList`) }}</span>
            </el-link>
            <div class="switch-job-btn">
              <el-link class="prev-job" :disabled="isPreview || loadingJobIds || jobChanging || currentJobIdIndex === 0" :underline="false" @click="handlePrevJob">
                <i class="prev-job-icon el-icon-arrow-left"></i>
                <span class="label">{{ $t(`${i18nPrefix}.label.prevJob`) }}</span>
              </el-link>
              <div v-rect-loading="loadingJobIds" element-loading-background="#fff" element-loading-custom-class="job-switch-loading" class="position">
                <span class="current">{{ (currentJobIdIndex + 1)|formatBigNumber }}</span>
                <span class="split">/</span>
                <span class="total">{{ totalCount|formatBigNumber }}</span>
              </div>
              <el-link class="next-job" :disabled="isPreview || loadingJobIds || jobChanging || (currentJobIdIndex + 1) === totalCount" :underline="false" @click="handleNextJob">
                <span class="label">{{ $t(`${i18nPrefix}.label.nextJob`) }}</span>
                <i class="next-job-icon el-icon-arrow-right"></i></el-link>
            </div>
          </div>
          <div class="job-base-info" ref="jobDetailBaseInfoRef">
            <div class="info-item job-name-referral">
              <div class="job-name">
                <div class="name-info">{{ jobDetailInfo.i18nName }}</div>
                <div v-if="jobDetailInfo.is_in_group" class="btn-inner single-job-group-tip">
                  {{ $t('jobDetail.fordApplyJobTipMsg') }}
                </div>
              </div>
              <div v-if="jobDetailInfo.to_show_referral" class="referral-btn">
                <el-popover trigger="hover" placement="left" popper-class="referral-info-content-popper">
                  <template #reference>
                    <i class="iconfont icona-bianzu23beifen11" @click="handleReferralBtnClick"></i>
                  </template>
                  <div v-if="refStepsSetting">
                    <ReferralSettings
                      class="referral-settings-content"
                      :refStepsSetting="refStepsSetting"
                      :company="company"
                      :hasCustomReferralWord="jobAndCompanyInfo.has_referral_word_customized"
                      :isHiddenRewards="isHiddenRewards"
                      :isUsePointReplacePayroll="isUsePointReplacePayroll"
                      :isFosunCompany="jobAndCompanyInfo.is_fosun_company"
                      :isCompanyEmployee="isEmployeeOfCompany"
                      :isADPCompany="isADPCompany"
                      :isCoachCompany="isCoachCompany"
                      :customizationRewardsRules="jobAndCompanyInfo.customization_rewards_rules"
                    ></ReferralSettings>
                  </div>
                </el-popover>
              </div>
            </div>
            <div class="info-item job-field-info">
              <div class="job-field">
                <div v-for="(item, index) in displayJobFieldInfoList" class="field-item" :key="index">
                  <el-tooltip
                    effect="light"
                    popper-class="light-tooltip-popper"
                    trigger="hover"
                    :content="item.label || $t(item.labelI18nKey)"
                  >
                    <span class="field-name">{{ item.i18nValue || $t(item.i18nKey) }}</span>
                  </el-tooltip>
                </div>
              </div>
              <div class="apply-and-link-policy">
                <div v-if="showApply" class="apply-btn-wrapper">
                  <div v-if="hasApply" class="apply-btn-container">
                    <div v-if="canReapply" class="btn-inner">
                      <el-button class="btn reapply-btn unified-btn" type="primary" @click="handleApplyBtnClick">{{ $t(`${i18nPrefix}.btn.reapply`) }}</el-button>
                    </div>
                    <div v-else-if="finishApply" class="btn-inner">
                      <el-button class="btn applied-btn unified-btn" type="primary">{{ $t(`${i18nPrefix}.btn.applied`) }}</el-button>
                    </div>
                    <div v-else class="btn-inner">
                      <el-button class="btn finished-btn unified-btn" type="primary" @click="handleContinueApplyBtnClick">{{ $t(`${i18nPrefix}.btn.continueFinishApply`) }}</el-button>
                    </div>
                  </div>
                  <div v-else class="apply-btn-container">
                    <el-button class="unified-btn apply-btn" type="primary" @click="handleApplyBtnClick">{{ $t(`${i18nPrefix}.btn.applyBtn`) }}</el-button>
                  </div>
                </div>
              </div>
            </div>
            <div class="info-item job-location-and-refer">
              <div class="job-location">
                <div v-auto-tooltip v-if="jobLocationList?.length === 1 && jobLocationList[0].city.i18nName" class="only-one-location" @click="handleOpenLocationMap(jobLocationList[0])">
                  <span class="location-icon" :class="[!canOpenLocation(jobLocationList[0]) && 'is-disabled']"><i class="iconfont iconAPP-location"></i></span>
                  <span class="location-name" :class="[!canOpenLocation(jobLocationList[0]) && 'is-disabled']">{{ jobLocationList[0].city.i18nName }}</span>
                </div>
                <div v-if="jobLocationList?.length > 1" class="location">
                  <div class="location-btn" @click="handleLocationBtnClick"><i class="iconfont iconAPP-location"></i></div>
                  <el-popover v-model="showLocationList" class="more-location-wrapper" trigger="hover" :visible-arrow="false" @click.native.stop>
                    <template #reference>
                      <span class="more-location">
                        <span v-auto-tooltip class="location-count">{{ $t('company.label.jobLocationCount', { count: jobLocationList.length })  }}</span>
                        <i class="expand-icon el-icon-arrow-down" :class="showLocationList && 'is-expand'"></i>
                      </span>
                    </template>
                    <div class="location-list">
                      <div
                        v-auto-tooltip
                        v-for="(location, index) in jobLocationList"
                        class="location"
                        :class="[!canOpenLocation(location) && 'is-disabled']"
                        :key="index"
                        @click="handleOpenLocationMap(location)"
                      >
                        {{ location.city ? location.city.i18nName : '' }}{{ location.address && ('-' + location.address) }}
                      </div>
                    </div>
                  </el-popover>
                </div>
              </div>
              <div class="refer-btn">
                <el-button v-if="jobDetailInfo.job_visibility !== 'I' && hasReferralPermission && showReferralBtn" class="submit-referral-btn unified-btn" type="primary" @click="handleReferralBtnClick">{{ $t(`${i18nPrefix}.btn.referralBtn`) }}</el-button>
              </div>
            </div>
          </div>
          <el-divider></el-divider>
          <div v-if="detailComponentList?.length" class="job-custom-webpage">
            <component
            v-for="(item, index) in detailComponentList"
            v-bind="item.props"
            class="body-content"
            :key="index"
            :ref="`component_${item.type}_${index}`"
            :is="item.component"
            />
          </div>
          <div v-else class="job-description" v-html="isEn() ? jobDetailInfo.description : jobDetailInfo.cn_description"></div>
        </div>
      </div>
      <div class="job-operation">
        <div v-if="!hiddenReferShare || (jobPosters && jobPosters.length > 0)"  class="card-container job-share">
          <div class="card-title share-tip">{{ $t('company.label.shareButton') }}</div>
          <ShareJobContent
            v-if="!hiddenReferShare"
            class="share-job-content"
            backOpenParamKey="open_referral"
            :style="{ height: `${jobBaseInfoHeight}px` }"
            :isPreview="isPreview"
            :jobId="jobId"
            :shareLink="referShareUrl"
            :normalShareUrl="normalShareUrl"
            :submitInfoUrl="submitInfoUrl"
            :isInChina="jobAndCompanyInfo.is_in_china"
            :companyId="company.id"
            :companyName="company.i18nName"
            :rootCompanyId="company.root_company.id"
            :jobName="jobDetailInfo.i18nName"
            :companyLogoPath="companyInfo.logo"
            :weChatShareMomentTitle="weChatShareMomentTitle"
            :shareToWeChatQrCodeUrl="shareToWeChatQrCodeUrl"
            :toShowReferral="jobDetailInfo.to_show_referral"
            :hiddenReferralRoleCheck="!hasReferralPermission"
          ></ShareJobContent>
          <!-- 公司海报 -->
          <JobPosterInfo
            v-if="jobPosters && jobPosters.length > 0"
            class="job-poster-info"
            ref="jobPoster"
            :shareLink="shareLink"
            :isPreview="isPreview"
            :jobDetailInfo="jobDetailInfo"
            :initJobPosters="jobPosters"
            :company="company"
            @fetchJobPoster="handleFetchJobPoster"
          >
            <template #poster-before="{loadJobPoster ,jobPosterList}">
              <el-divider v-if="!hiddenReferShare && (loadJobPoster || jobPosterList?.length > 0)"></el-divider>
            </template>
          </JobPosterInfo>
        </div>
        <div v-if="company.preference_survey.show_entrance" class="card-container job-alert">
          <div class="card-title">{{ $t('company.label.jobAlert') }}</div>
          <el-button class="job-alert-btn" @click="handelSurveyClick">{{ company.preference_survey.has_answered ? $t('company.btn.EditJobAlert') : $t('company.btn.createJobAlert') }}</el-button>
        </div>
        <div v-if="qrCode.loading || qrCode.src" class="card-container follow-us">
          <div class="card-title">{{ $t('company.label.followUs') }}</div>
          <div class="qr-code-image-wrapper">
            <img v-if="qrCode.src" class="qr-code-image" :src="qrCode.src" />
            <div v-else v-rect-loading="true" class="qr-code-image is-empty"></div>
          </div>
        </div>
      </div>
    </div>
    <!-- 弹窗协议 -->
    <PopupPolicyDialog
      :show.sync="showPopupPolicyDialog"
      :companyPolicyInfo="companyPolicyInfo"
      :isAgreeSystemPolicy="personalAgreementStatus?.is_agreed_system_policy"
      :showSystemPolicy="!personalAgreementStatus?.is_agreed_system_policy"
      @save="handleAgreeBtnClick"
      @cancel="handleCancelBtnClick"
    ></PopupPolicyDialog>
    <!-- 推荐奖励信息 dialog -->
    <DialogWrapper
      class="referral-reward-dialog"
      :show.sync="showReferralRewardDialog"
      :titleI18nKey="`${i18nPrefix}.label.referralRewardTitle`"
    >
      <div v-if="!hiddenReferralTip" class="referral-or-share-tip">
        {{ $t(`${i18nPrefix}.label.referralOrShareTip`) }}
      </div>
      <ReferralSettings
        hiddenTitle
        isInLine
        class="referral-settings-content"
        :refStepsSetting="refStepsSetting"
        :company="company"
        :hasCustomReferralWord="jobAndCompanyInfo.has_referral_word_customized"
        :isHiddenRewards="isHiddenRewards"
        :isUsePointReplacePayroll="isUsePointReplacePayroll"
        :isFosunCompany="jobAndCompanyInfo.is_fosun_company"
        :isCompanyEmployee="isEmployeeOfCompany"
        :isADPCompany="isADPCompany"
        :isCoachCompany="isCoachCompany"
        :customizationRewardsRules="jobAndCompanyInfo.customization_rewards_rules"
      ></ReferralSettings>
      <template #footer>
        <div class="footer">
          <el-button class="footer-btn cancel-btn" @click="handleShareBtnClick">{{ $t(`${i18nPrefix}.btn.shareBtn`) }}</el-button>
          <el-button v-if="showReferralSubmit" class="footer-btn confirm-btn" @click="handleSubmitBtnClick">{{ $t(`${i18nPrefix}.btn.submitBtn`) }}</el-button>
        </div>
      </template>
    </DialogWrapper>
    <!-- 推荐角色 dialog -->
    <DialogWrapper
      class="referral-role-dialog"
      :show.sync="showReferralRoleDialog"
      :titleI18nKey="`${i18nPrefix}.label.referralRoleTitle`"
      :saveBtnLabelI18nKey="`${i18nPrefix}.btn.yes`"
      :cancelBtnLabelI18nKey="`${i18nPrefix}.btn.no`"
      @save="handleSubmitIsEmployee"
      @cancel="handleSubmitNoEmployee"
      @closed="handleCloseReferralRoleDialog"
    >
      <div class="referral-role-content">{{ $t(`${i18nPrefix}.label.referralRoleTipContent`, { company: company.has_referral_share ? company.root_company.i18nName : company.i18nName }) }}</div>
    </DialogWrapper>
    <!-- 职位地点-地图 -->
    <LocationMap
      :show.sync="showJobLocationMap"
      :locationList="locationMapDialogInfo.locationList"
      :defaultLocation="locationMapDialogInfo.defaultLocation"
    ></LocationMap>
    <!-- 分享弹窗 - 分享到不同平台 -->
    <DialogWrapper
      class="share-to-platform-dialog"
      hideFooter
      width="38%"
      titleI18nKey="admin.job.editor.btn.shareBtn"
      :class="hiddenShareDialog && 'hidden-dialog'"
      :show.sync="showShareJobToPlatformDialog"
      :close-on-click-modal="false"
    >
      <div v-if="jobDetailInfo.to_show_referral && !jobAndCompanyInfo.has_referral_word_customized && !isHiddenRewards" class="tip">{{ $t('jobDetail.shareYourPersonallyParm', { balance: jobReferralRewardsTip }) }}</div>
      <div class="job-share-link">{{ shareToPlatformUrl }}</div>
      <el-divider class="divider"></el-divider>
      <div class="share-to-platform">
        <ShareJobContent
          v-if="!hiddenReferShare"
          class="share-job-content"
          hiddenReferralRoleCheck
          backOpenParamKey="open_referral"
          :jobId="jobId"
          :shareLink="shareToPlatformUrl"
          :normalShareUrl="normalShareUrl"
          :submitInfoUrl="submitInfoUrl"
          :isInChina="true"
          :rootCompanyId="company.root_company.id"
          :companyId="company.id"
          :companyName="company.has_referral_share ? company.root_company.i18nName : company.i18nName"
          :companyLogoPath="company.logo"
          :jobName="jobDetailInfo.i18nName"
          :weChatShareMomentTitle="weChatShareMomentTitle"
          :shareToWeChatQrCodeUrl="shareToWeChatQrCodeUrl"
          :toShowReferral="jobDetailInfo.to_show_referral"
        ></ShareJobContent>
        <!-- 公司海报 -->
        <JobPosterInfo
          v-if="jobPosters && jobPosters.length > 0"
          class="job-poster-dialog"
          ref="dialogJobPoster"
          :shareLink="shareLink"
          :company="company"
          :isPreview="isPreview"
          :jobDetailInfo="jobDetailInfo"
          :initJobPosters="jobPosters"
          @showPosterDialog="showPosterDialog"
          @closedPosterDialog="closedPosterDialog"
          @fetchJobPoster="handleFetchJobPoster(true)"
        >
        <template #poster-before="{loadJobPoster, jobPosterList}">
          <div v-if="loadJobPoster || jobPosterList?.length > 0">
            <el-divider v-if="!hiddenReferShare">{{ $t('common.or') }}</el-divider>
            <div class="job-poster-title">{{ $t(`${i18nPrefix}.label.jobPosterTitle`) }}</div>
          </div>
          </template>
        </JobPosterInfo>
      </div>
    </DialogWrapper>
    <!-- 确认创建提醒 -->
    <JobAlertConfirmDialog
      isPc
      :show.sync="showJobAlertDialog"
      :companyId="company.id"
      :logo="company.logo"
      :isAgreed="personalAgreementStatus?.is_agreed"
    ></JobAlertConfirmDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import { isEn, addI18nProperty, createI18nProperty, getI18nPropertyValue } from '@/i18n'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import PopupPolicyDialog from '../components/popupPolicyDialog.vue'
import ReferralSettings from '../components/referralSettings.vue'
import LocationMap from '../components/locationMap.vue'
import ShareJobContent from '../components/shareJobContent.vue'
import Slide from '@/components/company/detail/slide.vue'
import Video from '@/components/company/detail/video.vue'
import CustomJobDescription from '../components/customJobDescription.vue'
import JobAlertConfirmDialog from '@/components/job/jobDetail/jobAlertConfirmDialog.vue'
import { formatBigNumber } from '@/components/filters'
import { deviceType, addQueryInPath } from '@/utils/common'
import JobPosterInfo from '../components/jobPosterInfo.vue'
import { ROOT_COMPANY_ID } from '@/constant/company'

const ROLE_TYPE_MAP = {
  F: 'FULL_TIME',
  P: 'PART_TIME',
  I: 'INTERNSHIP'
}

const CUSTOM_FILTER_PREFIX = 'custom_filter_'

export default {
  components: {
    DialogWrapper,
    PopupPolicyDialog,
    ReferralSettings,
    LocationMap,
    ShareJobContent,
    Slide,
    Video,
    CustomJobDescription,
    JobAlertConfirmDialog,
    JobPosterInfo
  },
  props: {
    company: {
      required: true
    },
    isPreview: {
      type: Boolean,
      default: false
    },
    previewJobId: {
      required: false
    },
    previewJobDetailInfo: {
      required: false
    },
    previewJobAndCompanyInfo: {
      required: false
    },
    previewCompanyInfo: {
      required: false
    }
  },
  data () {
    return {
      isEn,
      show: false,
      i18nPrefix: 'admin.job.editor',
      showLocationList: false,
      jobAndCompanyInfo: {},
      jobDetailInfo: {
        i18nName: '',
        work_time: {
          i18nName: ''
        },
        compensation: {
          i18nName: ''
        },
        all_webpage_item: {
          en: null,
          zh: null
        }
      },
      companyInfo: {},
      isEmployeeOfCompany: false,
      refStepsSetting: null,
      qrCode: {
        loading: true,
        src: null
      },
      jobIdList: [],
      loadingJobIds: false,
      jobChanging: false,
      jobLocationList: [],
      normalShareUrl: '',
      referId: null,
      referShareUrl: '',
      submitInfoUrl: '',
      showApply: false,
      hasApply: false,
      canReapply: false,
      finishApply: false,
      isPopupPolicy: false,
      isLinkPolicy: false,
      showAgreementError: false,
      companyPolicyInfo: {},
      hasRefStepsSetting: false,
      showPopupPolicyDialog: false,
      agreedPolicy: [],
      showReferralRewardDialog: false,
      referralQrCodeUrl: '',
      showReferralQrCodeDialog: false,
      showReferralRoleDialog: false,
      showJobLocationMap: false,
      shareToQrCodeUrl: '',
      shareToPlatformUrl: '',
      showShareJobToPlatformDialog: false,
      locationMapDialogInfo: {
        locationList: [],
        defaultLocation: null
      },
      addPotentialTimer: null,
      jobBaseInfoHeight: 100,
      showJobAlertDialog: false,
      jobPosters: null,
      hiddenShareDialog: false,
      personalAgreementStatus: null,
      surveyInfo: {}
    }
  },
  computed: {
    ...mapGetters('account', ['isCandidateAccount']),
    shareToWeChatQrCodeUrl () {
      const query = this.$route.query
      let url = `/job-description/${this.jobDetailInfo.short_unique_id}`
      if (_.size(query) > 0) {
        url = `${url}?${_.map(query, (value, key) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')}`
      }
      const hashQuery = this.loadUrlQuery()
      const hashStr = this.getHashQuery(hashQuery)
      if (hashStr) {
        url = `${url}#${hashStr}`
      }
      return url
    },
    hiddenReferralTip () {
      if (this.company.root_company.id === 6143 && !this.isEmployeeOfCompany) {
        return true
      }
      return false
    },
    detailComponentList () {
      const list = []
      if (this.jobDetailInfo?.all_webpage_item) {
        const webpageItem = getI18nPropertyValue(this.jobDetailInfo.all_webpage_item, 'zh', 'en')
        if (webpageItem) {
          _.forEach(webpageItem, (item, index) => {
            let setting = null
            if (item.type === 'job-detail-description') {
              setting = {
                type: 'job-detail-description',
                component: CustomJobDescription,
                props: {
                  value: item,
                  showSurveyEntrance: this.jobAndCompanyInfo.show_survey_entrance,
                  hasAnswered: this.jobAndCompanyInfo.has_answered,
                  job: this.jobDetailInfo,
                  company: this.company,
                  isCompanyJdCustomized: this.companyInfo.is_company_jd_customized,
                  customJdCompanyId: this.companyInfo.custom_jd_company_id,
                  isRootCompanyJdCustomized: this.companyInfo.is_root_company_jd_customized
                }
              }
            } else if (item.type === 'silder') {
              setting = {
                type: 'slide',
                component: Slide,
                props: {
                  value: item,
                  hiddenIndicator: true,
                  slideArrow: 'hover'
                }
              }
            } else if (item.type === 'video') {
              setting = {
                type: 'video',
                component: Video,
                props: {
                  value: item
                }
              }
            }
            if (setting) {
              list.push(setting)
            }
          })
        }
      }
      return list
    },
    jobId () {
      if (this.isPreview) {
        return this.previewJobId
      }
      return this.$route.params.jobId
    },
    isHiddenRewards () {
      return Boolean(this.companyInfo?.premium?.hidden_rewards)
    },
    isUsePointReplacePayroll () {
      return Boolean(this.isEmployeeOfCompany && this.companyInfo?.premium?.use_point_replace_payroll)
    },
    isADPCompany () {
      return Boolean(this.companyInfo?.root_company?.id === 7496)
    },
    isCoachCompany () {
      return Boolean(this.companyInfo?.root_company?.id === 592)
    },
    showReferralSubmit () {
      if (this.company.root_company.id === 6143) {
        if (this.jobAndCompanyInfo.submit_referral && this.isEmployeeOfCompany) {
          return true
        } else {
          return false
        }
      } else if (this.jobAndCompanyInfo.submit_referral) {
        return true
      } else {
        return false
      }
    },
    totalCount () {
      return this.jobIdList?.length
    },
    currentJobIdIndex () {
      return _.findIndex(this.jobIdList, item => item === this.jobDetailInfo.id)
    },
    weChatShareMomentTitle () {
      const wxShareInfo = this.jobAndCompanyInfo.wechat_share_info ? JSON.parse(this.jobAndCompanyInfo.wechat_share_info) : {}
      return wxShareInfo?.moment_title || this.company.i18nName
    },
    hasLogin () {
      const account = this.getCurrentAccount()
      return Boolean(account?.id)
    },
    accountIsExits () {
      const account = this.getCurrentAccount()
      const softUser = this.getCurrentSoftUser()
      if (account?.id || softUser?.id) {
        return true
      } else {
        return false
      }
    },
    jobReferralRewardsTip () {
      const stepsSetting = this.refStepsSetting
      if (_.isEmpty(stepsSetting)) {
        return ''
      }

      let realBudget = null
      if (this.isEmployeeOfCompany) {
        realBudget = stepsSetting.employee_real_budget
      } else {
        realBudget = stepsSetting.non_employee_real_budget
      }

      const payroll = parseFloat(realBudget.payroll || 0)
      const points = parseInt(realBudget.points || 0)
      let cash = parseFloat(realBudget.cash || 0)
      cash += payroll
      const text = []
      if (cash > 0) {
        // 针对特殊的公司，需要在展示奖励的时候用 积分(points) 代替 元(yuan)，一般是针对员工这样展示
        if (this.isUsePointReplacePayroll) {
          text.push(this.$t('jobDetail.label.reward.points', { reward: formatBigNumber(parseInt(cash)) }))
        } else {
          text.push(this.$t('jobDetail.label.reward.cash', { reward: formatBigNumber(parseInt(cash)) }))
        }
      }
      if (points > 0) {
        text.push(this.$t('jobDetail.label.reward.points', { reward: formatBigNumber(points) }))
      }
      return text.join(', ')
    },
    displayJobFieldInfoList () {
      const list = []
      const companyJobFilters = this.jobDetailInfo.company_job_filters || []
      const customFields = this.jobDetailInfo.custom_fields || []
      const jobAndCompanyInfo = this.isPreview ? this.previewJobAndCompanyInfo : this.jobAndCompanyInfo
      const vm = this
      _.forEach(companyJobFilters, filter => {
        if (filter.name === 'Location') {
          return
        }
        if (filter.name === 'Work Time') {
          const workTime = {
            labelI18nKey: `${this.i18nPrefix}.label.jobDetailWorkTime`,
            ...this.jobDetailInfo.work_time,
            order: filter.display_order_in_jd
          }
          if (this.jobDetailInfo.work_time) {
            addI18nProperty(workTime, 'i18nValue', 'cn_name', 'en_name')
          } else {
            createI18nProperty(workTime, 'i18nValue', `${this.i18nPrefix}.label.experienceUnlimited`)
          }
          list.push(workTime)
        } else if (filter.name === 'Salary' && jobAndCompanyInfo.show_salary) {
          list.push(addI18nProperty({
            labelI18nKey: `${this.i18nPrefix}.label.jobDetailSalary`,
            ...this.jobDetailInfo.compensation,
            order: filter.display_order_in_jd
          }, 'i18nValue', 'cn_name', 'en_name'))
        } else if (filter.name === 'Role Type') {
          list.push({
            labelI18nKey: `${this.i18nPrefix}.label.jobDetailRoleType`,
            i18nKey: `${this.i18nPrefix}.roleType.${this.jobDetailInfo.roleType}`,
            order: filter.display_order_in_jd
          })
        } else if (filter.name === 'Experience') {
          const displayValue = {
            labelI18nKey: `${this.i18nPrefix}.label.jobDetailExperience`,
            order: filter.display_order_in_jd
          }
          Object.defineProperty(displayValue, 'i18nValue', {
            enumerable: true,
            configurable: true,
            get () {
              return vm.getJobExperience(vm.jobDetailInfo.experience)
            }
          })
          list.push(displayValue)
        } else if (filter.name === 'Updated Time') {
          const displayValue = {
            labelI18nKey: `${this.i18nPrefix}.label.jobUpdateTime`,
            order: filter.display_order_in_jd,
            i18nValue: vm.formatTime(vm.jobDetailInfo.updated_time)
          }
          list.push(displayValue)
        } else {
          const customField = _.find(customFields, item => item.name === filter.name)
          const filterValue = customField ? customField.filter_value : null
          const filterValueItems = customField ? customField.filter_value_items : []
          const displayValue = addI18nProperty(filter, 'label', 'cn_name', 'name')
          filter.order = filter.display_order_in_jd
          if (_.includes(['SELECT', 'SINGLE'], filter.type) && filterValueItems?.length) {
            Object.defineProperty(displayValue, 'i18nValue', {
              enumerable: true,
              configurable: true,
              get () {
                return _.chain(filterValueItems).map(item => _.get(item, isEn() ? 'name' : 'cn_name')).join(isEn() ? ', ' : '，').value()
              }
            })
            list.push(displayValue)
          } else if (filterValue) {
            Object.defineProperty(displayValue, 'i18nValue', {
              enumerable: true,
              configurable: true,
              get () {
                return filterValue
              }
            })
            list.push(displayValue)
          }
        }
      })
      return _.orderBy(list, ['order'], 'desc')
    },
    showReferralBtn () {
      if (this.jobDetailInfo.to_show_referral) {
        return true
      }
      const buttonItems = getI18nPropertyValue(this.jobDetailInfo.all_button_items, 'zh', 'en')
      const referralBtnSetting = _.find(buttonItems, item => item.key === 'referral_btn')
      if (referralBtnSetting) {
        return referralBtnSetting.show
      }
      return true
    },
    hasReferralPermission () {
      return this.companyInfo.premium?.referral
    },
    shareLink () {
      return this.referShareUrl || this.normalShareUrl
    },
    hiddenReferShare () {
      const rootCompanyId = this.companyInfo.root_company?.id || this.previewCompanyInfo.root_company?.id
      return rootCompanyId ? rootCompanyId === ROOT_COMPANY_ID.MERCEDES_BENZ : true
    }
  },
  created () {
    this.$eventBus.$emit('showLoading', false)
    this.initPage()
  },
  mounted () {
    this.applyJobListener = this.$eventBus.$on('applyJob', this.handleApplyBtnClick)
    window.addEventListener('resize', this.resetJobBaseInfoHeight)
  },
  destroyed () {
    clearTimeout(this.addPotentialTimer)
    if (this.applyJobListener) {
      this.$off('applyJob', this.applyJobListener)
    }
    window.removeEventListener('resize', this.resetJobBaseInfoHeight)
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount', 'getCurrentSoftUser']),
    ...mapActions('job', [
      'getJobDetailInfoByShortId',
      'getJobReferralSetting',
      'getJobIdList',
      'getJobApplyStatus',
      'getReferralEarnRewards',
      'getJobQrCode',
      'addPotentialCandidates',
      'getJobDetailJobPoster'
    ]),
    ...mapActions('company', ['getCompanyPolicyInfo']),
    ...mapActions('wechat', ['getCompanyWxQrCode']),
    ...mapActions('chatbot', ['chatbotClickApplyJobButton']),
    ...mapActions('eventTrack', ['trackCandidateViewJobDetail']),
    ...mapActions('jobPosterAmbassador', ['checkAccountIsAmbassador', 'uploadAmbassadorJobPoster', 'deleteAmbassadorJobPoster']),
    ...mapActions('personal', ['getAgreementStatus']),
    ...mapActions('survey', ['getSurveyInfo']),
    async initPage () {
      clearTimeout(this.addPotentialTimer)
      await this.initJobDetailInfo()
      if (deviceType.isMobile) {
        const url = `/job-description/${this.jobDetailInfo.short_unique_id}/?company_id=${this.company.id}`
        this.$router.push({ path: url, query: this.$route.query, hash: this.$route.hash })
        return
      }
      if (!this.isPreview) {
        this.trackCandidateViewJobDetail({ short_id: this.jobDetailInfo.short_unique_id })
      }
      this.show = true
      await this.initData()
      this.scrollToTop()
      this.addPotentialTimer = setTimeout(() => {
        const params = { refer_id: this.referId, job_id: this.jobId, company_id: this.company.id }
        this.addPotentialCandidates(params)
      }, 20000)
      const query = this.loadUrlQuery()
      if (query.open_referral === '1') {
        if (!this.jobAndCompanyInfo.submit_referral) {
          this.handleShareBtnClick()
        } else {
          this.showReferralRewardDialog = true
        }
        const newQuery = _.omit(query, 'open_referral')
        const hashStr = this.getHashQuery(newQuery)
        let path = `/recruiting/company/${this.company.id}/job/${this.jobId}/job-detail`
        const params = {
          aj_source: this.$route.query.aj_source,
          aj_code: this.$route.query.aj_code
        }
        path = addQueryInPath(path, params, true)
        window.history.pushState({}, '', `${path}#${hashStr}`)
      }
      const account = this.getCurrentAccount()
      this.$emit('getJDChatbotContext', {
        jobId: this.jobId,
        showReferral: this.jobDetailInfo.to_show_referral && this.jobDetailInfo.job_visibility !== 'I',
        submitReferral: this.jobAndCompanyInfo.submit_referral,
        context: {
          type: this.jobDetailInfo.hasPrescreenJobSurvey ? 'GET_QUESTIONNAIRE' : null,
          job_id: this.jobDetailInfo.id,
          survey_id: this.jobDetailInfo.hasPrescreenJobSurvey && this.jobDetailInfo.prescreenJobSurvey && this.jobDetailInfo.prescreenJobSurvey.id,
          candidate_id: account.type === 'candidate' ? account.id : '0',
          question_id: '',
          answer: '',
          autoSendText: (this.jobDetailInfo.hasPrescreenJobSurvey && this.jobDetailInfo.prescreenJobSurvey && this.jobDetailInfo.prescreenJobSurvey.id && '进入职位调查问卷') || ''
        }
      })
      this.resetAllBtnStyle()
      this.resetJobBaseInfoHeight()
    },
    async initData () {
      this.loadWxQrCode()
      this.initReferralSetting()
      this.loadJobIdList()
      this.loadCompanyPolicyInfo()
      this.initPersonalAgreementStatus()
      this.initSurveyInfo()
      await this.loadJobApplyStatus()
      this.loadJobPoster()
    },
    scrollToTop () {
      const scrollEl = document.scrollingElement
      if (scrollEl) {
        const headerEl = document.querySelector('.layout-head')
        const bannerEl = document.querySelector('.layout-banner')
        let height = 0
        if (headerEl) {
          height += headerEl.clientHeight
        }
        if (bannerEl) {
          height += bannerEl.clientHeight
        }
        scrollEl.scrollTop = height
      }
    },
    resetAllBtnStyle () {
      const btnElList = document.querySelectorAll('.unified-btn')
      _.forEach(btnElList, btnEl => {
        btnEl.style.width = 'auto'
      })
      const maxWidth = btnElList.length > 0 ? _.maxBy(btnElList, 'offsetWidth').offsetWidth : null
      if (maxWidth) {
        _.forEach(btnElList, btnEl => {
          btnEl.style.width = `${maxWidth}px`
        })
      }
    },
    resetJobBaseInfoHeight () {
      // content baseInfo 高度 - 两者padding，margin的差值
      const jobDetailHeaderRef = this.$refs.jobDetailHeaderRef
      const jobDetailBaseInfoRef = this.$refs.jobDetailBaseInfoRef
      if (jobDetailHeaderRef && jobDetailBaseInfoRef) {
        this.jobBaseInfoHeight = jobDetailBaseInfoRef.clientHeight - 12
      }
    },
    async loadJobApplyStatus () {
      if (!this.jobDetailInfo.short_unique_id) {
        return
      }
      const params = { short_id: this.jobDetailInfo.short_unique_id, refer_id: this.$route.query.referId }
      const resp = await this.getJobApplyStatus(params)
      if (resp) {
        this.normalShareUrl = resp.normal_share_url
        this.referId = resp.referId
        this.showApply = resp.show_apply === 1
        this.hasApply = resp.has_apply === 1
        this.canReapply = resp.can_reapply
        this.finishApply = resp.finish_apply === 1
        const params = {
          aj_source: this.$route.query.aj_source,
          aj_code: this.$route.query.aj_code
        }
        if ((!_.includes(this.normalShareUrl, 'aj_source') || !_.includes(this.normalShareUrl, 'aj_code')) && params.aj_source && params.aj_code) {
          this.normalShareUrl = addQueryInPath(this.normalShareUrl, params, true)
        }
      }
      await this.loadJobReferUrl()
    },
    async loadCompanyPolicyInfo () {
      const resp = await this.getCompanyPolicyInfo({ companyId: this.company.id })
      if (resp) {
        if (_.has(resp, 'popup')) {
          this.isPopupPolicy = true
        } else if (_.has(resp, 'link')) {
          this.isLinkPolicy = true
        }
        this.companyPolicyInfo = resp
      }
    },
    async initJobDetailInfo () {
      this.$eventBus.$emit('showLoading', true)
      try {
        if (this.isPreview) {
          this.jobAndCompanyInfo = this.previewJobAndCompanyInfo || {}
          this.jobDetailInfo = this.previewJobDetailInfo || {}
          this.companyInfo = this.previewCompanyInfo || {}
        } else {
          const jobInfoParams = {
            shortId: this.jobId,
            params: {
              job_id: this.jobId,
              company_id: this.company.id
            }
          }
          const ajSource = this.$route.query.aj_source
          const ajCode = this.$route.query.aj_code
          if (ajSource && ajCode) {
            jobInfoParams.params.aj_source = ajSource
            jobInfoParams.params.aj_code = ajCode
          }
          const resp = await this.getJobDetailInfoByShortId(jobInfoParams)
          if (!resp) {
            this.$message.error(this.$t('error.message.serverError'))
            return
          }
          this.jobAndCompanyInfo = resp.data || {}
          this.jobDetailInfo = resp.data?.job || {}
          this.companyInfo = resp.data?.company || {}
        }
        if (this.companyInfo) {
          addI18nProperty(this.companyInfo, 'i18nName', 'cn_name', 'name')
        }
        if (this.jobDetailInfo) {
          this.jobDetailInfo.roleType = ROLE_TYPE_MAP[this.jobDetailInfo.role_type]
          addI18nProperty(this.jobDetailInfo, 'i18nName', 'cn_title', 'en_title')
          if (this.jobDetailInfo.work_time) {
            addI18nProperty(this.jobDetailInfo.work_time, 'i18nName', 'cn_name', 'en_name')
          } else {
            createI18nProperty(this.jobDetailInfo.work_time, 'i18nName', `${this.i18nPrefix}.label.experienceUnlimited`)
          }
          if (this.jobDetailInfo.job_locations) {
            _.forEach(this.jobDetailInfo.job_locations, (item) => {
              if (item.city) {
                addI18nProperty(item.city, 'i18nName', 'cn_name', 'name')
              }
              if (item.region) {
                addI18nProperty(item.region, 'i18nName', 'cn_name', 'name')
              }
              if (item.cbd) {
                addI18nProperty(item.cbd, 'i18nName', 'cn_name', 'name')
              }
            })
            this.jobLocationList = this.jobDetailInfo.job_locations
          }

          if (this.jobDetailInfo.compensation) {
            addI18nProperty(this.jobDetailInfo.compensation, 'i18nName', 'cn_name', 'en_name')
          }
          if (this.jobAndCompanyInfo.jd_customize && !this.isPreview) {
            const customizedHeaderEn = this.jobDetailInfo.customized_header_en ? JSON.parse(this.jobDetailInfo.customized_header_en) : null
            const customizedHeaderCn = this.jobDetailInfo.customized_header_cn ? JSON.parse(this.jobDetailInfo.customized_header_cn) : null
            const customizedFooterEn = this.jobDetailInfo.customized_footer_en ? JSON.parse(this.jobDetailInfo.customized_footer_en) : null
            const customizedFooterCn = this.jobDetailInfo.customized_footer_cn ? JSON.parse(this.jobDetailInfo.customized_footer_cn) : null
            if (!this.jobDetailInfo?.all_webpage_item) {
              this.jobDetailInfo.all_webpage_item = {
                en: [],
                zh: []
              }
            }
            let webpageItemEn = this.jobDetailInfo.all_webpage_item?.en || []
            let webpageItemCn = this.jobDetailInfo.all_webpage_item?.zh || []

            if (webpageItemEn.length === 1 && webpageItemEn[0].type === 'job-detail-description') {
              webpageItemEn = []
            }

            if (webpageItemCn.length === 1 && webpageItemCn[0].type === 'job-detail-description') {
              webpageItemCn = []
            }

            if (webpageItemEn?.length) {
              if (customizedHeaderEn) {
                const headerIndex = _.findIndex(webpageItemEn, item => item.type === 'job-detail-description')
                webpageItemEn.splice(headerIndex, 0, ...customizedHeaderEn)
              }
              if (customizedFooterEn) {
                const footerIndex = _.findIndex(webpageItemEn, item => item.type === 'job-detail-description')
                webpageItemEn.splice(footerIndex + 1, 0, ...customizedFooterEn)
              }
            } else {
              if (customizedHeaderEn) {
                webpageItemEn.push(...customizedHeaderEn)
              }
              webpageItemEn.push({ type: 'job-detail-description' })
              if (customizedFooterEn) {
                webpageItemEn.push(...customizedFooterEn)
              }
            }

            if (webpageItemCn?.length) {
              if (customizedHeaderCn) {
                const headerIndex = _.findIndex(webpageItemCn, item => item.type === 'job-detail-description')
                webpageItemCn.splice(headerIndex, 0, ...customizedHeaderCn)
              }
              if (customizedFooterCn) {
                const footerIndex = _.findIndex(webpageItemCn, item => item.type === 'job-detail-description')
                webpageItemCn.splice(footerIndex + 1, 0, ...customizedFooterCn)
              }
            } else {
              if (customizedHeaderCn) {
                webpageItemCn.push(...customizedHeaderCn)
              }
              webpageItemCn.push({ type: 'job-detail-description' })
              if (customizedFooterCn) {
                webpageItemCn.push(...customizedFooterCn)
              }
            }
            this.jobDetailInfo.all_webpage_item.en = webpageItemEn
            this.jobDetailInfo.all_webpage_item.zh = webpageItemCn
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async initReferralSetting () {
      if (!this.jobDetailInfo.short_unique_id) {
        return
      }
      const resp = await this.getJobReferralSetting({
        short_id: this.jobDetailInfo.short_unique_id
      })
      if (resp) {
        this.refStepsSetting = resp.ref_steps_setting
        this.isEmployeeOfCompany = resp.is_employee_of_company
        if (resp.ref_steps_setting && this.jobDetailInfo.to_show_referral) {
          this.hasRefStepsSetting = true
        }
        if (this.isEmployeeOfCompany) {
          this.rewardContentBalance = this.refStepsSetting?.employee_real_budget
        } else {
          this.rewardContentBalance = this.refStepsSetting?.non_employee_real_budget
        }
        this.jobToShowReferral = this.jobDetailInfo.to_show_referral && !this.isInternalJob
      }
    },
    async loadWxQrCode () {
      this.qrCode.loading = true
      try {
        const resp = await this.getCompanyWxQrCode({
          company_id: this.company.id
        })
        if (resp.data) {
          this.qrCode.src = 'data:image/png;base64,' + resp.data
        }
      } finally {
        this.qrCode.loading = false
      }
    },
    loadUrlQuery () {
      const hashStr = decodeURIComponent(this.$route.hash)
      const query = {}
      _.chain(hashStr.substring(1)).split('&').map(item => item.split('=')).forEach((paramArr) => {
        const paramName = paramArr[0]
        let paramVal = null
        if (paramArr.length > 1) {
          paramVal = paramArr[1]
        }
        query[paramName] = paramVal
      }).value()
      return query
    },
    async loadJobPoster () {
      const params = {
        company_id: this.company.id,
        language_code: isEn() === 'en' ? 'en' : 'cn'
      }
      const resp = await this.getJobDetailJobPoster(params)
      if (resp) {
        this.jobPosters = resp.job_poster_list
      }
    },
    async initPersonalAgreementStatus () {
      if (!this.hasLogin || !this.isCandidateAccount) {
        return
      }
      const resp = await this.getAgreementStatus({
        company_id: this.company.id
      })
      if (resp) {
        this.personalAgreementStatus = resp
      }
    },
    async initSurveyInfo () {
      if (!this.hasLogin || !this.isCandidateAccount) {
        return
      }
      const resp = await this.getSurveyInfo(this.company.id)
      if (resp) {
        this.surveyInfo = resp
      }
    },
    handleFetchJobPoster (isDialogJobPoster) {
      const jobPosterRef = isDialogJobPoster ? this.$refs.jobPoster : this.$refs.dialogJobPoster
      if (!jobPosterRef) {
        this.jobPosters = []
      } else {
        jobPosterRef.loadJobPosterInfo(this.shareLink)
      }
    },
    getHashQuery (query) {
      const hashStr = _.chain(query).map((val, key) => {
        if (key === 'open_referral') {
          return
        }
        if (val === null || val === undefined) {
          val = ''
          if (key === 'ji' || key === 'jt') {
            val = 0
          }
        }
        return `${key}=${encodeURIComponent(val)}`
      }).filter().join('&').value()
      return hashStr
    },
    setSearchParams (query) {
      const params = {
        page: query.page,
        page_size: query.page_size,
        company_id: this.company.id,
        keywords: query.keywords,
        type: query.type,
        job_industry_id: query.ji || 0,
        job_type_id: query.jt || 0,
        job_salary_id: query.js,
        job_work_time_id: query.jwt,
        role_type: query.rt,
        order_by: query.order_by,
        area_city_id: query.city,
        area_region_id: query.region,
        area_id: query.area,
        only_referral: query.refj === '1' ? 1 : null,
        jtitle: query.jtitle,
        jid: query.jid
      }
      _.forEach(query, (val, key) => {
        if (key.startsWith(CUSTOM_FILTER_PREFIX)) {
          params[key] = val
        }
      })
      return params
    },
    async loadJobIdList () {
      this.loadingJobIds = true
      const query = this.loadUrlQuery()
      const params = this.setSearchParams(query)
      try {
        const resp = await this.getJobIdList(params)
        if (resp) {
          this.jobIdList = resp
        }
      } finally {
        this.loadingJobIds = false
      }
    },
    async loadJobReferUrl () {
      if (!this.accountIsExits || !this.jobId) {
        return
      }
      let url = `/referral/earn-rewards/${this.jobId}/`
      const referId = this.referId
      if (referId && referId !== 'None') {
        url = url + referId + '/'
      }
      url = `${url}?company_id=${this.company.id}`
      const resp = await this.getReferralEarnRewards(url)
      if (resp.status === 'success') {
        if (resp.refer_url) {
          this.referShareUrl = this.formatShareUrl(resp.refer_url)
        }
      }
    },
    formatShareUrl (url) {
      let shareUrl = location.origin + url
      if (this.normalShareUrl.includes('?')) {
        const urlParmsStr = this.normalShareUrl.split('?')
        const pammStr = urlParmsStr[1]
        _.forEach(pammStr[1].split('&'), item => {
          if (['aj_source', 'aj_code'].includes(item)) {
            if (shareUrl.includes('?')) {
              shareUrl = shareUrl + '&' + item
            } else {
              shareUrl = shareUrl + '?' + item
            }
          }
        })
      }
      return shareUrl
    },
    handleBackToJobList () {
      const query = this.loadUrlQuery()
      const hashStr = this.getHashQuery(query)
      const backUrl = this.$route.query.backUrl
      const path = backUrl || `/recruiting/company/${this.company.id}/job-list`
      const routeQuery = _.omit(this.$route.query, 'backUrl')
      this.$router.push({
        path: path,
        hash: hashStr,
        query: routeQuery
      })
    },
    async handlePrevJob () {
      if (this.loadingJobIds || this.jobChanging || !this.jobIdList.length) {
        return
      }
      const prevJobId = this.jobIdList[this.currentJobIdIndex - 1]
      if (!prevJobId) {
        return
      }
      const query = this.loadUrlQuery()
      const hashStr = this.getHashQuery(query)
      this.jobChanging = true
      try {
        await this.$router.push({ path: `/recruiting/company/${this.company.id}/job/${prevJobId}/job-detail`, query: this.$route.query, hash: hashStr })
      } catch (error) {
        this.jobChanging = false
        throw error
      }
    },
    async handleNextJob () {
      if (this.loadingJobIds || this.jobChanging || !this.jobIdList.length) {
        return
      }
      const nextJobId = this.jobIdList[this.currentJobIdIndex + 1]
      if (!nextJobId) {
        return
      }
      const query = this.loadUrlQuery()
      const hashStr = this.getHashQuery(query)
      this.jobChanging = true
      try {
        await this.$router.push({ path: `/recruiting/company/${this.company.id}/job/${nextJobId}/job-detail`, query: this.$route.query, hash: hashStr })
      } catch (error) {
        this.jobChanging = false
        throw error
      }
    },
    formatTime (time) {
      if (!time) {
        return ''
      }
      return moment(time).format('YYYY-MM-DD')
    },
    getJobExperience (experience) {
      if (experience === '-1') {
        return this.$t(`${this.i18nPrefix}.label.experienceUnlimited`)
      }
      return this.$t(`${this.i18nPrefix}.label.experienceLabel`, { experience })
    },
    handelSurveyClick () {
      if (this.isPreview || (this.hasLogin && !this.personalAgreementStatus)) {
        return
      }
      if (this.surveyInfo.survey?.has_answered) {
        if (this.company.root_company?.id === ROOT_COMPANY_ID.MSD) {
          this.$router.push('/msdchina/join-talent-community')
        } else {
          this.$router.push(`/recruiting/company/${this.company.id}/survey`)
        }
      } else {
        this.showJobAlertDialog = true
      }
    },
    handleReferralBtnClick () {
      if (this.isPreview) {
        return
      }
      if (this.accountIsExits) {
        if (this.referShareUrl) {
          if (this.jobAndCompanyInfo.submit_referral) {
            // 登录则展示提交分享弹窗
            this.showReferralRewardDialog = true
            return
          }
          this.showShareBox(this.referShareUrl)
        } else {
          this.showShareAction()
        }
      } else {
        // 未登录直接展示询问是否员工弹窗
        // 当推荐收费项未开时，点击分享，不需要提问是否是员工
        if (this.hasReferralPermission) {
          this.showReferralRoleDialog = true
        } else {
          this.handleSubmitNoEmployee()
        }
      }
    },
    async showNormalShareBox () {
      let shareLink = this.normalShareUrl || window.location.href
      if (this.referShareUrl) {
        shareLink = this.referShareUrl
      } else if (this.accountIsExits) {
        shareLink = this.submitInfoUrl
      }
      this.showShareBox(shareLink)
    },
    async showShareBox (link) {
      const referUrl = link || this.referShareUrl
      this.getJobQrCode({ jobId: this.jobId, shareUrl: referUrl }).then(resp => {
        if (resp && resp.status === 'success') {
          this.shareToQrCodeUrl = resp.qr_code + '?_=' + (new Date()).getTime()
        }
      })
      this.showReferralRewardDialog = false
      this.shareToPlatformUrl = referUrl
      this.showShareJobToPlatformDialog = true
      this.hiddenShareDialog = false
    },
    async showShareAction () {
      let referUrl = this.referShareUrl
      if (referUrl) {
        if (!referUrl.includes('http')) {
          referUrl = location.origin + referUrl
          this.referShareUrl = referUrl
        }
        await this.showShareBox(referUrl)
        return
      }
      let url = `/referral/earn-rewards/${this.jobId}/`
      const referId = this.referId
      if (referId && referId !== 'None') {
        url = url + referId + '/'
      }
      url = `${url}?company_id=${this.company.id}`
      if (this.accountIsExits && this.jobId) {
        const res = await this.getReferralEarnRewards(url)
        if (res.status === 'success') {
          if (res.refer_url) {
            referUrl = this.formatShareUrl(res.refer_url)
            this.referShareUrl = referUrl
            await this.showShareBox(referUrl)
          }
        } else {
          await this.showShareBox(this.normalShareUrl)
        }
      } else {
        await this.showShareBox(this.normalShareUrl)
      }
    },
    handleShareBtnClick () {
      if (!this.accountIsExits || !this.jobDetailInfo.to_show_referral) {
        this.showNormalShareBox()
      } else {
        if (this.referShareUrl) {
          this.showShareBox(this.referShareUrl)
        } else {
          this.showShareAction()
        }
      }
    },
    handleSubmitBtnClick () {
      const url = `/recruiting/personal/referral/submit/resume?job_id=${this.jobId}&back=${encodeURIComponent(location.href)}&company_id=${this.company.root_company.id}`
      window.location.href = url
      this.showReferralRewardDialog = false
    },
    handleSubmitIsEmployee () {
      // 员工跳转登录，open_referral 登录成功后回调直接打开分享弹窗
      let backUrl = window.location.href
      if (backUrl.includes('#=')) {
        backUrl = `${backUrl}&open_referral=1`
      } else {
        backUrl = `${backUrl}#=&open_referral=1`
      }

      window.location.href = `/passport/employee/register/?from_job=1&back=${encodeURIComponent(backUrl)}&company_id=${this.company.root_company.id}`
    },
    handleSubmitNoEmployee () {
      if (this.jobDetailInfo.to_show_referral) {
        let backUrl = window.location.href
        if (backUrl.includes('#=')) {
          backUrl = `${backUrl}&open_referral=1`
        } else {
          backUrl = `${backUrl}?open_referral=1`
        }
        window.location.href = `/passport/candidate/register/?back=${encodeURIComponent(backUrl)}`
        return
      }
      if (this.jobAndCompanyInfo.submit_referral) {
        this.showReferralRewardDialog = true
      } else {
        this.showNormalShareBox()
      }
    },
    handleCloseReferralRoleDialog () {
      this.showReferralRoleDialog = false
    },
    handleApplyBtnClick () {
      if (this.isPreview) {
        return
      }
      let applyUrl = `/a/${this.jobDetailInfo.short_unique_id}/`
      if (this.referId) {
        applyUrl = `${applyUrl}${this.referId}/`
      }
      applyUrl = `${applyUrl}?need_edit_resume=1`
      const params = {
        aj_source: this.$route.query.aj_source,
        aj_code: this.$route.query.aj_code
      }
      applyUrl = addQueryInPath(applyUrl, params, true)
      if (!this.isPopupPolicy && !this.isLinkPolicy) {
        window.location.href = applyUrl
      } else {
        this.showPopupPolicyDialog = true
      }
    },
    handleContinueApplyBtnClick () {
      if (this.isPreview) {
        return
      }
      let applyUrl = `/a/${this.jobDetailInfo.short_unique_id}/`
      if (this.referId) {
        applyUrl = `${applyUrl}${this.referId}/`
      }
      const params = {
        aj_source: this.$route.query.aj_source,
        aj_code: this.$route.query.aj_code
      }
      applyUrl = addQueryInPath(applyUrl, params, true)
      window.location.href = applyUrl
    },
    handleCancelBtnClick () {
      this.showPopupPolicyDialog = false
    },
    async handleAgreeBtnClick ({ policyItemIds, isAgreedSystemPolicy }) {
      let applyUrl = `/a/${this.jobDetailInfo.short_unique_id}/`
      if (this.referId) {
        applyUrl = `${applyUrl}${this.referId}/`
      }
      applyUrl = `${applyUrl}?need_edit_resume=1&agreed_policy_items=${policyItemIds.join(',')}&is_agreed_system_policy=${isAgreedSystemPolicy || ''}`
      const params = {
        aj_source: this.$route.query.aj_source,
        aj_code: this.$route.query.aj_code
      }
      applyUrl = addQueryInPath(applyUrl, params, true)
      window.location.href = applyUrl
      this.showPopupPolicyDialog = false
    },
    handleLocationBtnClick () {
      if (this.companyInfo.is_overseas_company) {
        return
      }
      if (this.isPreview) {
        return
      }
      this.locationMapDialogInfo.locationList = this.jobLocationList
      this.locationMapDialogInfo.defaultLocation = null
      this.showJobLocationMap = true
    },
    canOpenLocation (location) {
      if (!location) {
        return false
      }
      const lng = parseFloat(location.lng)
      const lat = parseFloat(location.lat)
      if (!lng || !lat) {
        return false
      }
      return true
    },
    showPosterDialog () {
      this.hiddenShareDialog = true
    },
    closedPosterDialog () {
      this.showShareJobToPlatformDialog = false
    },
    handleOpenLocationMap (location) {
      if (this.companyInfo.is_overseas_company || this.isPreview || !this.canOpenLocation(location)) {
        return
      }
      this.locationMapDialogInfo.locationList = this.jobLocationList
      this.locationMapDialogInfo.defaultLocation = location
      this.showJobLocationMap = true
    }
  },
  watch: {
    jobId (jobId) {
      if (!jobId || this.isPreview) {
        return
      }
      this.jobChanging = true
      this.initPage().finally(() => {
        this.jobChanging = false
      })
    },
    '$i18n.locale': {
      handler () {
        this.$nextTick(() => {
          this.resetAllBtnStyle()
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-detail-wrapper {
  .job-detail-container {
    display: flex;

    .job-info {
      flex: 1;
      margin-right: 18px;
      padding: 24px 24px 42px 32px;
      border-radius: 6px;
      background: #ffffff;
      border: 1px solid #e4e7ed;
      overflow: hidden;

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .back-to-job-list {
          .back-job-list-icon {
            color: var(--custom-color);
          }

          .label {
            margin-left: 6px;
          }

          ::v-deep {
            .el-link--inner {
              color: var(--custom-color);
            }
          }
        }

        .switch-job-btn {
          display: flex;
          align-items: center;

          ::v-deep .job-switch-loading {
            .el-loading-spinner {
              margin-top: -12px;
            }

            .loading-spinner .spinner {
              width: 27px;
              height: 24px;

              > div {
                width: 4px;
              }
            }
          }

          .prev-job {
            .label {
              margin-left: 6px;
            }

            .prev-job-icon {
              color: var(--custom-color);
            }

            .el-icon-arrow-left {
              color: var(--custom-color);
            }

            ::v-deep {
              .el-link--inner {
                color: var(--custom-color);
              }
            }
          }

          .position  {
            margin: 0 40px;
            line-height: 24px;
            color: #606266;
            font-size: 14px;

            .split {
              margin: 0 10px;
            }
          }

          .next-job {
            .label {
              margin-right: 6px;
            }

            .next-job-icon {
              color: var(--custom-color);
            }

            ::v-deep {
              .el-link--inner {
                color: var(--custom-color);
              }
            }
          }
        }
      }

      .job-base-info {
        margin-bottom: 40px;

        .info-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .job-name-referral {
          margin-bottom: 20px;
          height: 26px;

          .job-name {
            .name-info {
              font-weight: bold;
              font-size: 18px;
              color: #303133;
            }

            .single-job-group-tip {
              margin-bottom: 20px;
              color: red;
              font-size: 12px;
            }
          }

          .referral-btn {
            margin-right: -2px;
            cursor: pointer;

            .iconfont {
              color: var(--custom-color);
              font-size: 26px;
            }
          }
        }

        .job-field-info {
          .job-field {
            display: flex;
            align-items: center;
            flex-wrap: wrap;

            .field-item {
              .field-name {
                display: inline;
                word-break: keep-all;
                word-wrap: break-word;
                border-right: 1px solid #c0c4cc;
                padding-right: 12px;
                margin-right: 12px;
              }

              &:first-child {
                .field-name {
                  padding-left: 0;
                }
              }

              &:last-child {
                .field-name {
                  border-right: none;
                  padding-right: 0;
                }
              }
            }
          }

          .apply-and-link-policy {
            .link-policy {
              text-align: right;

              .policy-checkbox-group {
                .policy-checkbox {
                  .policy-content {
                    font-size: 12px;
                    color: #303313;

                    ::v-deep {
                      p {
                        margin: 0;
                        padding: 0;
                      }
                    }
                  }
                }
              }

              .errorText {
                color: red;
                font-size: 12px;
                text-align: right;
              }
            }

            .apply-btn-wrapper {
              .apply-btn-container {
                .btn-inner {
                  .btn {
                    height: 32px;
                    background: var(--custom-color);
                    padding: 0 28px;
                    border-color: var(--custom-color);
                  }

                  .applied-btn {
                    cursor: not-allowed;
                  }
                }

                .apply-btn {
                  height: 32px;
                  background: var(--custom-color);
                  padding: 0 28px;
                  border-color: var(--custom-color);
                }
              }
            }
          }
        }

        .job-location-and-refer {
          height: 32px;

          .job-location {
            .only-one-location {
              .location-icon {
                cursor: pointer;
                display: inline-block;
                vertical-align: middle;
                color: var(--custom-color);
                margin-right: 5px;

                .iconfont {
                  font-size: 20px;
                }
              }

              .location-name {
                cursor: pointer;
              }

              .is-disabled {
                cursor: text;
              }
            }

            .location {
              .location-btn {
                display: inline-block;
                vertical-align: middle;
                color: var(--custom-color);
                margin-right: 5px;
                cursor: pointer;

                .iconfont {
                  font-size: 20px;
                }
              }

              .more-location-wrapper {
                display: inline-block;
                vertical-align: middle;

                ::v-deep .el-popover__reference-wrapper {
                  display: inline-block;
                  max-width: 100%;
                }

                .more-location {
                  display: inline-flex;
                  max-width: 100%;
                  cursor: pointer;
                  overflow: hidden;

                  .location-count {
                  }

                  .expand-icon {
                    margin-left: 10px;
                    transition: transform .3s ease;
                    line-height: inherit;

                    &.is-expand {
                      transform: rotateZ(180deg);
                    }
                  }
                }

              }
            }
          }

          .refer-btn {
            .submit-referral-btn {
              height: 32px;
              background: var(--custom-color);
              padding: 0 28px;
              border-color: var(--custom-color);
            }
          }
        }
      }

      .job-description {
        margin-top: 24px;

        ::v-deep {
          ul, ol {
            list-style: disc;
            margin-top: 0px;
            margin-bottom: 0px;
            padding-left: 20px;
          }

          p {
            margin-top: 0px;
            margin-bottom: 0px;
          }
        }
      }

      .job-custom-webpage {
        width: auto;
        margin: auto;

        .body-content {
          margin-bottom: 25px;

          &:last-child {
            margin-bottom: 0;
          }

          ::v-deep {
            .el-carousel__item {
              display: block;

              img {
                width: 100%;
              }
            }
          }
        }
      }
    }

    .job-operation {
      .card-container {
        width: 396px;
        padding: 24px;
        border-radius: 4px;
        background: #ffffff;
        border: 1px solid #e4e7ed;

        + .card-container {
          margin-top: 24px;
        }

        .card-title {
          margin-bottom: 20px;
          font-weight: 500;
        }
      }

      .job-share {
        .share-tip {
          margin-bottom: 54px;
        }

        .share-job-content {
          padding: 0 26px;

          .share-line {
            margin-bottom: 16px;
          }
        }

        .job-poster-info {
          ::v-deep {
            &.job-poster-page {
              .job-poster {
                .poster-preview-container {
                  .image-container {
                    .image-wrapper {
                      > img {
                        max-height: 160px;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      .job-alert {
        .job-alert-btn {
          width: 100%;
          padding: 10px;
          border-color: var(--custom-color);
          border-radius: 2px;
          color: var(--custom-color);
          font-size: 16px;
          line-height: 22px;

          &:hover {
            --rgb-custom-color: rgb(from var(--custom-color) r g b / 5%);
            background-color: var(--rgb-custom-color);
          }
        }
      }

      .follow-us {
        .qr-code-image-wrapper {
          padding: 16px;
          --rgb-custom-color: rgb(from var(--custom-color) r g b / 5%);
          background: var(--rgb-custom-color);

          .qr-code-image {
            display: block;
            width: 180px;
            height: 180px;
            margin: auto;

            &.is-empty {
              background: #fff;
            }
          }
        }
      }
    }
  }
}

.referral-reward-dialog {
  .referral-or-share-tip {
    font-size: 18px;
    font-weight: 400;
    color: #606266;
    margin-bottom: 18px;
    word-break: break-word;
    white-space: normal;
    word-wrap: break-word;
  }

  .referral-settings-content {
    min-height: 80px;

    ::v-deep {
      .rewards-container {
        .referral-info-list {
          list-style: disc;
          padding-left: 20px;
          font-size: 18px;
          font-weight: 400;
          list-style: disc;
          color: #606266;
          list-style: none;

          .referral-item {
            margin-bottom: 18px;

            &::before {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #c0c4cc;
              display: inline-block;
              margin-right: 10px;
              vertical-align: middle;
              content: '';
              margin-left: -20px;
            }

            .referral-info {
              display: inline-block;

              .referral-title {
                display: inline-block;
                vertical-align: middle;
                margin-right: 8px;
              }

              .referral-tip {
                display: inline-block;
                vertical-align: middle;
                font-size: 16px;
                color: #C0C4CC;
              }
            }

            &::marker {
              color: #C0C4CC;
              font-size: 22px;
            }
          }
        }
      }
    }
  }
  .footer {
    .footer-btn {
      font-size: 14px;
      border: 0;
      cursor: pointer;
      margin-left: 24px;
      line-height: 14px;
      padding: 10px 30px;
      background: var(--custom-color);
      color: #fff;
    }
  }
}

.referral-qrcode-dialog {
  .qrcode-wrapper {
    text-align: center;

    img {
      width: 300px;
    }
  }
}

.referral-role-dialog {
  .referral-role-content {
    min-height: 80px;
    font-size: 14px;
    font-weight: 400;
    color: #303313;
  }

  ::v-deep {
    .dialog-footer {
      .cancel-btn {
        background-color: var(--custom-color);
        color: #fff;
        border: 1px solid var(--custom-color);
      }

      .save-btn {
        background-color: var(--custom-color);
        color: #fff;
        border: 1px solid var(--custom-color);

        &:hover {
          background: var(--custom-color);
          border-color: var(--custom-color);
        }
      }
    }
  }
}

.share-to-platform-dialog {
  &.hidden-dialog {
    display: none;
  }

  .share-tip-container {
    padding: 20px;
    background: var(--custom-color);

    .company-logo {
      display: inline-block;
      color: #fff;

      .logo {
        width: 60px;
        border-radius: 50%;
        height: 60px;
        vertical-align: middle;
      }
    }

    .company-info {
      display: inline-block;
      color: #fff;
      margin-left: 20px;
      line-height: 20px;
      vertical-align: middle;
      width: 380px;

      .job-title {
        font-size: 16px;
        font-weight: bold;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .company-name {
        font-size: 15px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .job-poster-dialog {
    .job-poster-title {
      color: #606266;
      margin-bottom: 24px;
    }

    ::v-deep {
      .job-poster {
        margin: 0 78px;
      }
    }
  }

  .tip {
    font-size: 16px;
  }

  .job-share-link {
    font-size: 16px;
    margin-top: 10px;
  }

  .title {
    font-size: 14px;
    margin-bottom: 30px;
  }

  .el-divider{
    background: #dcdfe6;
  }

  .share-to-platform {
    .share-job-content {
      padding: 0 78px;
      flex: 1;

      ::v-deep {
        .share-line {
          .share {
            .iconfont {
              font-size: 54px;
            }
          }
        }
      }
    }
  }
}

.location-list {
  max-width: 300px;
  max-height: 200px;
  padding: 4px 8px;
  overflow: hidden auto;

  .location {
    color: #606266;
    cursor: pointer;

    + .location {
      margin-top: 14px;
    }
  }

  .is-disabled {
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
.referral-info-content-popper {
  border: 1px solid var(--custom-color) !important;

  .popper__arrow {
    border-left-color: var(--custom-color) !important;
  }

  p {
    margin: 0;
    padding: 0;
  }

  .referral-settings-content {
    .rewards-container {
      color: #444444;
      font-size: 12px;

      .rewards-title {
        color: #444444;
        font-size: 15px;
        white-space: nowrap;
      }

      .referral-info-list {
        list-style: none;

        .referral-item {
          margin: 5px;

          &:last-child {
            margin-bottom: 0;
          }

          &::before {
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: #444444;
            display: inline-block;
            margin-right: 10px;
            vertical-align: middle;
            content: "";
            margin-bottom: 0px;
          }

          .referral-info {
            vertical-align: middle;
            display: inline-block;

            .referral-title {
              font-size: 15px;
            }

            .referral-tip {
              font-size: 13px;
              margin-left: 10px;
            }
          }
        }

        .has-more {
          &::before {
            margin-bottom: 18px;
          }
        }
      }
    }
  }
}
</style>
